(() => {
  "use strict";

  const existing = window.GLLFProtectionContractV2;
  if (existing && existing.cartContractVersion === "gllf.protection-cart.v2") {
    if (!window.GLLFProtectionContract) Object.defineProperty(window, "GLLFProtectionContract", { value: existing, configurable: false, writable: false });
    if (typeof document !== "undefined" && typeof document.dispatchEvent === "function" && typeof CustomEvent === "function") {
      document.dispatchEvent(new CustomEvent("gllf:protection-contract-ready", { detail: { contractVersion: existing.cartContractVersion, moduleVersion: existing.moduleVersion } }));
    }
    return;
  }

  // New writes identify RC19.4.5. The immediately previous identifier remains
  // readable during rolling cutover without being emitted on new cart lines.
  const moduleVersion = "13.9-RC19.4.5";
  const cartContractVersion = "gllf.protection-cart.v2";
  const previousCartContractVersion = "gllf.protection-cart.v1";
  const selectionStatus = "SERVER QUOTE CONSENT RECORDED";
  const authoritativeProductIds = Object.freeze({
    "Shipping Protection": "10405563990312",
    "1-Year Grow Guarantee": "10405571461416",
  });
  const compatibleModuleVersions = Object.freeze([moduleVersion, "13.9-RC19.3.1"]);
  const prop = (line, key) => String(line?.properties?.[key] ?? "");
  const numericId = (candidate) => String(candidate ?? "").split("/").pop();
  const split = (value) => String(value || "").split("|").filter(Boolean);
  const issue = (issues, condition, code) => { if (!condition) issues.push(code); };

  function programType(line) {
    const value = prop(line, "_Protection Type").toLowerCase();
    if (value.includes("shipping")) return "Shipping Protection";
    if (value.includes("grow") || value.includes("warranty") || value.includes("1-year plant plan")) return "1-Year Grow Guarantee";
    return "";
  }

  function cartContractFor(line) {
    return prop(line, "_Protection Cart Contract") || previousCartContractVersion;
  }

  function supportsModuleVersion(value) {
    return compatibleModuleVersions.includes(String(value || ""));
  }

  function validateChargeDetailed(charge, parent, now = Date.now()) {
    const issues = [];
    const acceptedUntil = Date.parse(prop(charge, "_Protection Accepted Until"));
    const handoffUntil = Date.parse(prop(charge, "_Protection Handoff Expires At"));
    const type = programType(charge);
    const contractVersion = cartContractFor(charge);
    const currentContract = contractVersion === cartContractVersion;
    const expectedProductId = authoritativeProductIds[type];
    const quoteId = prop(charge, "_Protection Quote ID");
    const parentRefs = split(prop(parent, "_Protection Parent Refs"));
    const parentQuoteIds = split(prop(parent, "_Protection Quote IDs"));
    const parentContractVersion = cartContractFor(parent);
    const quoteIndex = parentQuoteIds.indexOf(quoteId);
    const protectedValue = prop(charge, "_Protection Value Basis") === "WHOLESALE_QUALIFIED_FINAL"
      ? (parent.final_line_price ?? parent.original_line_price)
      : parent.original_line_price;
    issue(issues, [cartContractVersion, previousCartContractVersion].includes(contractVersion), "PROTECTION_CART_CONTRACT_UNSUPPORTED");
    issue(issues, [cartContractVersion, previousCartContractVersion].includes(parentContractVersion), "PROTECTION_PARENT_CART_CONTRACT_UNSUPPORTED");
    issue(issues, parentContractVersion === contractVersion, "PROTECTION_CART_CONTRACT_MISMATCH");
    issue(issues, /^pq_[a-f0-9]{32}$/i.test(quoteId), "PROTECTION_QUOTE_ID_INVALID");
    issue(issues, prop(charge, "_Protection Selection ID").length >= 8, "PROTECTION_SELECTION_ID_MISSING");
    issue(issues, prop(charge, "_Protection Handoff").length >= 20, "PROTECTION_HANDOFF_MISSING");
    issue(issues, Number.isFinite(acceptedUntil) && acceptedUntil > now, "PROTECTION_SELECTION_EXPIRED");
    issue(issues, Number.isFinite(handoffUntil) && handoffUntil > now, "PROTECTION_HANDOFF_EXPIRED");
    issue(issues, prop(charge, "_Protection Consent") === "SELECTED", "PROTECTION_CONSENT_MISSING");
    issue(issues, numericId(prop(charge, "_Parent Product ID")) === String(parent.product_id), "PROTECTION_PARENT_PRODUCT_MISMATCH");
    issue(issues, numericId(prop(charge, "_Parent Variant ID")) === String(parent.variant_id), "PROTECTION_PARENT_VARIANT_MISMATCH");
    issue(issues, numericId(prop(charge, "_Protection Charge Variant ID")) === String(charge.variant_id), "PROTECTION_CHARGE_VARIANT_MISMATCH");
    issue(issues, Number(prop(charge, "_Protected Quantity")) === Number(parent.quantity), "PROTECTION_PARENT_QUANTITY_MISMATCH");
    issue(issues, Number(prop(charge, "_Protected Merchandise Value")) === Number(protectedValue), "PROTECTION_VALUE_MISMATCH");
    issue(issues, supportsModuleVersion(prop(charge, "_GLLF Module Version")), "PROTECTION_MODULE_UNSUPPORTED");
    issue(issues, type && Boolean(expectedProductId), "PROTECTION_PROGRAM_INVALID");
    // These mirrored fields were added in v2. During the rolling window v1
    // remains readable, while the actual Shopify line product is authoritative
    // for both versions.
    if (currentContract) issue(issues, numericId(prop(charge, "_Protection Charge Product ID")) === numericId(charge.product_id), "PROTECTION_CHARGE_PRODUCT_PROPERTY_MISMATCH");
    issue(issues, numericId(charge.product_id) === expectedProductId, "PROTECTION_CHARGE_PRODUCT_NOT_AUTHORITATIVE");
    issue(issues, Boolean(prop(charge, "_Protection Group")) && prop(charge, "_Protection Group") === prop(parent, "_Protection Group"), "PROTECTION_GROUP_MISMATCH");
    issue(issues, quoteIndex >= 0, "PROTECTION_PARENT_QUOTE_MISMATCH");
    issue(issues, parentQuoteIds.length === parentRefs.length && quoteIndex >= 0 && parentRefs[quoteIndex] === prop(charge, "_Protection Parent Ref"), "PROTECTION_PARENT_CORRELATION_MISMATCH");
    issue(issues, prop(parent, "_Protection Selection Status") === selectionStatus, "PROTECTION_PARENT_SELECTION_STATUS_INVALID");
    if (currentContract) issue(issues, Boolean(prop(parent, "_Protection Destination Fingerprint")), "PROTECTION_DESTINATION_FINGERPRINT_MISSING");
    issue(issues, Number.isSafeInteger(Number(prop(charge, "_Protection Quote Amount"))) && Number(prop(charge, "_Protection Quote Amount")) > 0, "PROTECTION_QUOTE_AMOUNT_INVALID");
    if (charge.original_line_price != null && charge.final_line_price != null) issue(issues, Number(charge.original_line_price) === Number(charge.final_line_price), "PROTECTION_DISCOUNT_APPLIED");
    return Object.freeze({ ok: issues.length === 0, code: issues[0] || "VERIFIED", issues: Object.freeze([...new Set(issues)]), contractVersion });
  }

  const validateCharge = (charge, parent, now = Date.now()) => validateChargeDetailed(charge, parent, now).ok;

  /* gpc-coverage-limit: on a large order a plan covers plant value up to a limit. The limit is written on the plan's
     own line so it shows in the cart, at checkout and on the order. Small orders get no extra line. */
  const usd = (cents) => "$" + (Number(cents) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const coverageLimitProperty = (quote) => {
    const covered = Number(quote && quote.coveredValueCents), full = Number(quote && quote.protectedValueCents);
    if (!quote || quote.coverageCapped !== true || !Number.isSafeInteger(covered) || !Number.isSafeInteger(full) || covered < 1 || covered >= full) return {};
    return { "Coverage limit": `Covers up to ${usd(covered)} of the ${usd(full)} in plants on this line` };
  };

  const buildChargeProperties = (input) => Object.freeze({
    "_Protection Cart Contract": cartContractVersion,
    "_Protection Type": input.type,
    "_Protection Group": input.group,
    "_Protection Quote ID": input.quote.quoteId,
    "_Protection Selection ID": input.selection.selectionId,
    "_Protection Parent Ref": input.selection.parentCorrelation,
    "_Protection Handoff": input.selection.orderHandoff,
    "_Protection Quote Expires At": input.quote.expiresAt,
    "_Protection Accepted Until": input.selection.acceptedUntil,
    "_Protection Handoff Expires At": input.selection.handoffExpiresAt,
    "_Protection Quote Amount": String(input.selection.amountCents),
    "_Protection Consent": "SELECTED",
    "_Parent Product ID": input.quote.productId,
    "_Parent Variant ID": input.quote.variantId,
    "_Protected Quantity": String(input.quote.quantity),
    "_Protected Merchandise Value": String(input.selection.protectedValueCents),
    "_Protection Value Basis": input.selection.protectedValueBasis,
    "_Protection Rate": String(input.selection.ratePercent) + "%",
    "_Protection Price Tier": input.selection.tier,
    "_Protection Terms Version": input.selection.termsVersion,
    "_Protection Disclosure Version": input.selection.disclosureVersion,
    "_Protection Charge Product ID": input.selection.chargeProductId,
    "_Protection Charge Variant ID": input.part.variantId,
    "_Protection Qualification ID": input.selection.qualificationId || "RETAIL",
    "_Protection Qualification Digest": input.selection.qualificationDigest || "RETAIL",
    "_GLLF Module Version": moduleVersion,
    ...coverageLimitProperty(input.quote),
  });

  const buildHandoffAttributes = (selections) => Object.freeze(
    Object.fromEntries(selections.map((selection) => [
      `_GLLF Protection Handoff ${selection.quoteId}`,
      selection.orderHandoff,
    ])),
  );

  const buildParentProtectionProperties = (input) => {
    const shipping = input.programs.includes("SHIPPING_PROTECTION");
    const oneYear = input.programs.includes("ONE_YEAR_PLANT_PLAN");
    const oneYearIndex = input.programs.indexOf("ONE_YEAR_PLANT_PLAN");
    const oneYearQuote = input.quotes.find((quote) => quote.program === "ONE_YEAR_PLANT_PLAN");
    const oneYearSelection = oneYearIndex >= 0 ? input.selections[oneYearIndex] : null;
    const academyComplete = Boolean(oneYear && (
      oneYearSelection?.academyVerified === true ||
      Boolean(oneYearSelection?.academyEvidence) ||
      oneYearQuote?.academyVerified === true
    ));
    return Object.freeze({
      "_Protection Cart Contract": cartContractVersion,
      "_Protection Group": input.group,
      "_Protection Selection Status": selectionStatus,
      "_Protection Quote IDs": input.quotes.map((quote) => quote.quoteId).join("|"),
      "_Protection Parent Refs": input.selections.map((selection) => selection.parentCorrelation).join("|"),
      "_Protection Destination Fingerprint": input.quotes[0].destinationFingerprint,
      "_Protection Value Basis": input.selections[0].protectedValueBasis,
      "_Shipping Protection": shipping ? "Selected — this line only" : "Not selected",
      "_1-Year Plant Warranty": oneYear ? "Selected — this line only" : "Not selected",
      "_Care Academy Status": oneYear ? (academyComplete ? "COMPLETE" : "INCOMPLETE") : "NOT REQUIRED",
      "_Care Academy Version": academyComplete ? (oneYearSelection?.academyVersion || oneYearQuote?.academyVersion || "SERVER VERIFIED") : "NOT REQUIRED",
      "_GLLF Module Version": moduleVersion,
    });
  };

  const api = Object.freeze({
    moduleVersion,
    cartContractVersion,
    previousCartContractVersion,
    compatibleModuleVersions,
    authoritativeProductIds,
    selectionStatus,
    supportsModuleVersion,
    validateCharge,
    validateChargeDetailed,
    buildChargeProperties,
    buildHandoffAttributes,
    buildParentProtectionProperties,
  });

  Object.defineProperty(window, "GLLFProtectionContractV2", { value: api, configurable: false, writable: false });
  if (!window.GLLFProtectionContract) Object.defineProperty(window, "GLLFProtectionContract", { value: api, configurable: false, writable: false });
  if (typeof document !== "undefined" && typeof document.dispatchEvent === "function" && typeof CustomEvent === "function") {
    document.dispatchEvent(new CustomEvent("gllf:protection-contract-ready", { detail: { contractVersion: cartContractVersion, moduleVersion } }));
  }
})();
